import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLeaveMasterEntry } from './hooks/useLeaveMasterEntry';

const PRESET_COLORS = [
  { name: 'Blue', hex: '#3B82F6' },
  { name: 'Emerald', hex: '#10B981' },
  { name: 'Amber', hex: '#F59E0B' },
  { name: 'Red', hex: '#EF4444' },
  { name: 'Purple', hex: '#8B5CF6' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'Cyan', hex: '#06B6D4' },
  { name: 'Indigo', hex: '#6366F1' },
];

export default function LeaveMasterEntryScreen() {
  const { state, actions } = useLeaveMasterEntry();
  const navigate = useNavigate();
  const { form, isEdit, isLoading, isSaving } = state;

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center p-16">
        <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        <p className="mt-3 text-xs font-semibold text-on-surface-variant">Loading Leave Details...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header Banner */}
      <section className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Edit Leave Type' : 'Create Leave Type'}
          </h1>
        </div>

        <Link
          to="/payroll/leave-master"
          className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface-variant hover:text-primary hover:bg-surface-container-low transition shadow-xs w-fit"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          <span>Back to List</span>
        </Link>
      </section>

      {/* 2-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Form Card (lg:col-span-8) */}
        <div className="lg:col-span-8 rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm overflow-hidden flex flex-col justify-between">
          {/* Card Header */}
          <div className="border-b border-outline-variant/20 bg-surface-container-low/40 px-8 py-4">
            <h2 className="font-display text-lg font-bold text-on-surface">Leave Master Details</h2>
          </div>

          {/* Form Body */}
          <div className="p-8">
            <div className="max-w-xl mx-auto space-y-5">
              {/* Leave Name */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Leave Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => actions.patchForm('name', e.target.value)}
                  placeholder="e.g. Sick Leave"
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Alias */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Alias
                </label>
                <input
                  type="text"
                  value={form.alias}
                  onChange={(e) => actions.patchForm('alias', e.target.value)}
                  placeholder="e.g. SL"
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Color Picker with Swatches */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-start gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right pt-2">
                  Color
                </label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 max-w-xs">
                    <input
                      type="text"
                      value={form.color}
                      onChange={(e) => actions.patchForm('color', e.target.value)}
                      placeholder="#3B82F6"
                      className="h-9 flex-1 rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs font-mono text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 uppercase"
                    />
                    <label className="relative flex items-center justify-center h-9 w-9 rounded-lg border border-outline-variant/30 bg-surface-container-low cursor-pointer overflow-hidden shadow-xs hover:scale-105 transition">
                      <input
                        type="color"
                        value={form.color.startsWith('#') ? form.color : `#${form.color}`}
                        onChange={(e) => actions.patchForm('color', e.target.value)}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                      />
                      <span
                        className="h-5 w-5 rounded-full border border-black/15 shadow-inner"
                        style={{ backgroundColor: form.color || '#3B82F6' }}
                      />
                    </label>
                  </div>

                  {/* Preset color swatches */}
                  <div className="flex items-center gap-2 flex-wrap pt-1">
                    <span className="text-[11px] font-semibold text-on-surface-variant mr-1">Presets:</span>
                    {PRESET_COLORS.map((c) => {
                      const isSelected = (form.color || '').toLowerCase() === c.hex.toLowerCase();
                      return (
                        <button
                          key={c.hex}
                          type="button"
                          title={c.name}
                          onClick={() => actions.patchForm('color', c.hex)}
                          className={`h-6 w-6 rounded-full border transition-all cursor-pointer ${
                            isSelected
                              ? 'scale-115 ring-2 ring-primary ring-offset-2 border-white'
                              : 'border-black/10 hover:scale-110'
                          }`}
                          style={{ backgroundColor: c.hex }}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons Row */}
          <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 bg-surface-container-low/40 px-8 py-4">
            <button
              type="button"
              disabled={isSaving}
              onClick={() => actions.handleSave({ exitAfter: true })}
              className="h-9 px-6 bg-[#0047ab] hover:bg-[#003788] text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-sm">save</span>
              <span>{isSaving ? 'Saving...' : 'Save & Exit'}</span>
            </button>

            {!isEdit && (
              <button
                type="button"
                disabled={isSaving}
                onClick={() => actions.handleSave({ exitAfter: false })}
                className="h-9 px-5 bg-surface-container-lowest hover:bg-surface-container-low text-primary font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/40 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Save &amp; Continue
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate('/payroll/leave-master')}
              className="h-9 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/20"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right Written Context Panel (lg:col-span-4) */}
        <div className="lg:col-span-4 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-on-surface">
            <span className="material-symbols-outlined text-base text-amber-500 font-bold">
              lightbulb
            </span>
            <span>TIPS:</span>
          </div>

          <p className="text-xs text-on-surface-variant leading-relaxed font-medium">
            Define organizational leave categories (e.g. Casual Leave, Sick Leave, Earned Leave) for policy allocation and employee leave balance tracking.
          </p>

          <div className="pt-3 border-t border-outline-variant/15 space-y-2">
            <h4 className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              GUIDELINES:
            </h4>
            <ul className="text-xs text-on-surface-variant space-y-2 list-disc list-inside leading-relaxed">
              <li>
                <strong className="text-on-surface">Leave Name:</strong> Specify official title for the leave category.
              </li>
              <li>
                <strong className="text-on-surface">Color Swatch:</strong> Choose a distinctive color code for calendar and roster visualization.
              </li>
              <li>
                <strong className="text-on-surface">Alias:</strong> Use standard short acronyms (e.g. SL, CL, EL, ML) for compact leave status chips.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
